/**
 * useReplies Hook
 * Fetches replies to a specific event following NIP-10
 */

import { useState, useEffect, useCallback } from 'react'
import { Event } from 'nostr-tools'
import { getNostrPool } from '../lib/nostrPool'
import { DEFAULT_RELAYS } from '../config/constants'

interface UseRepliesResult {
  replies: Event[]
  isLoading: boolean
  refetch: () => void
}

import { useGlobalModeration } from './useGlobalModeration';

export function useReplies(eventId: string | null): UseRepliesResult {
  const [replies, setReplies] = useState<Event[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [refreshKey, setRefreshKey] = useState(0)
  const { blockedEventIds, blockedPubkeys } = useGlobalModeration();
  const blockedEventIdsSize = blockedEventIds.size;

  const refetch = useCallback(() => {
    setRefreshKey(prev => prev + 1)
  }, [])

  useEffect(() => {
    if (!eventId) {
      setReplies([])
      return
    }

    const pool = getNostrPool()
    setIsLoading(true)
    setReplies([])

    const fetchedReplies: Event[] = []

    const filter = {
      kinds: [1],
      '#e': [eventId],
      limit: 100
    }

    const sub = pool.subscribeMany(DEFAULT_RELAYS, filter, {
      onevent(event: Event) {
        // Global Moderation
        if (blockedEventIds.has(event.id)) return;
        if (blockedPubkeys.has(event.pubkey)) return;

        // Verify this is actually a reply to our event
        const eTags = event.tags.filter(t => t[0] === 'e')
        const isValidReply = eTags.some(t => t[1] === eventId)

        if (isValidReply) {
          fetchedReplies.push(event)
          // Update state with sorted replies
          setReplies([...fetchedReplies].sort((a, b) => a.created_at - b.created_at))
        }
      },
      oneose() {
        setIsLoading(false)
      }
    })

    // Cleanup
    return () => {
      sub.close()
    }
  }, [eventId, refreshKey, blockedEventIdsSize])

  return { replies, isLoading, refetch }
}

/**
 * useReplyCount Hook
 * Just get the count of replies without full data
 */
export function useReplyCount(eventId: string | null): { count: number; isLoading: boolean } {
  const [count, setCount] = useState(0)
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    if (!eventId) {
      setCount(0)
      return
    }

    const pool = getNostrPool()
    setIsLoading(true)
    let replyCount = 0

    const filter = {
      kinds: [1],
      '#e': [eventId],
      limit: 100
    }

    const sub = pool.subscribeMany(DEFAULT_RELAYS, filter, {
      onevent(event: Event) {
        const eTags = event.tags.filter(t => t[0] === 'e')
        if (eTags.some(t => t[1] === eventId)) {
          replyCount++
          setCount(replyCount)
        }
      },
      oneose() {
        setIsLoading(false)
      }
    })

    return () => {
      sub.close()
    }
  }, [eventId])

  return { count, isLoading }
}
